import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../components/ui/tooltip";
import axios from "axios";
import { Loader2, AlertTriangle, Clock, DollarSign, Phone, ArrowRight, RefreshCw, Info, MapPin, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const RISK_STYLES = {
  high: { badge: "bg-red-100 text-red-700 border-red-200", card: "border-l-4 border-l-red-500", icon: "text-red-500" },
  medium: { badge: "bg-amber-100 text-amber-700 border-amber-200", card: "border-l-4 border-l-amber-500", icon: "text-amber-500" },
  low: { badge: "bg-blue-100 text-blue-700 border-blue-200", card: "border-l-4 border-l-blue-500", icon: "text-blue-500" },
};

const METRIC_DESCRIPTIONS = {
  stalled_deals: "Projects that haven't had any activity (stage change, update, or notes) in the last 14+ days. These need attention to keep them moving through the pipeline.",
  at_risk_value: "The total estimated dollar value of all stalled projects. This is potential revenue that could be lost if these deals are not re-engaged.",
  high_risk: "Projects stalled for 30+ days with no activity. These are in serious danger of being lost and need immediate follow-up - a phone call or site visit is recommended.",
  medium_risk: "Projects stalled for 14-30 days. These are starting to cool off and should be followed up with soon to prevent them from becoming high risk.",
};

export default function PipelineHealth() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHealth = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/analytics/pipeline-health`);
      setData(res.data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load pipeline health");
    }
    setLoading(false);
  };

  useEffect(() => { fetchHealth(); }, []);

  const formatCurrency = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(v);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#0F4C81]" /></div>;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="min-h-screen">
        <Header title="Pipeline Health">
          <Button variant="outline" className="h-10" onClick={fetchHealth} data-testid="refresh-health-btn">
            <RefreshCw className="w-4 h-4 mr-2" />Refresh
          </Button>
        </Header>

        <div className="p-4 sm:p-6 space-y-6">
          {/* Summary Cards with hover tooltips */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Tooltip>
              <TooltipTrigger asChild>
                <Card className={`cursor-help ${data?.total_stalled > 0 ? "bg-red-50 border-red-200" : ""}`} data-testid="metric-stalled-deals">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 flex items-center gap-1">Stalled Deals <Info className="w-3 h-3 text-slate-400" /></p>
                        <p className="text-2xl font-bold mt-1 text-slate-800">{data?.total_stalled || 0}</p>
                      </div>
                      <AlertTriangle className={`w-8 h-8 ${data?.total_stalled > 0 ? "text-red-500" : "text-slate-300"}`} />
                    </div>
                  </CardContent>
                </Card>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs text-sm p-3">{METRIC_DESCRIPTIONS.stalled_deals}</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Card className="cursor-help" data-testid="metric-at-risk-value">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 flex items-center gap-1">At-Risk Value <Info className="w-3 h-3 text-slate-400" /></p>
                        <p className="text-2xl font-bold mt-1 text-slate-800">{formatCurrency(data?.total_at_risk_value || 0)}</p>
                      </div>
                      <DollarSign className="w-8 h-8 text-amber-500" />
                    </div>
                  </CardContent>
                </Card>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs text-sm p-3">{METRIC_DESCRIPTIONS.at_risk_value}</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Card className={`cursor-help ${data?.high_risk_count > 0 ? "bg-red-50 border-red-200" : ""}`} data-testid="metric-high-risk">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 flex items-center gap-1">High Risk <Info className="w-3 h-3 text-slate-400" /></p>
                        <p className="text-2xl font-bold mt-1 text-red-600">{data?.high_risk_count || 0}</p>
                      </div>
                      <AlertTriangle className="w-8 h-8 text-red-400" />
                    </div>
                  </CardContent>
                </Card>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs text-sm p-3">{METRIC_DESCRIPTIONS.high_risk}</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Card className="cursor-help" data-testid="metric-medium-risk">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 flex items-center gap-1">Medium Risk <Info className="w-3 h-3 text-slate-400" /></p>
                        <p className="text-2xl font-bold mt-1 text-amber-600">{data?.medium_risk_count || 0}</p>
                      </div>
                      <Clock className="w-8 h-8 text-amber-400" />
                    </div>
                  </CardContent>
                </Card>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs text-sm p-3">{METRIC_DESCRIPTIONS.medium_risk}</TooltipContent>
            </Tooltip>
          </div>

          {/* Stalled Deals List - Clickable projects */}
          {data?.stalled_deals?.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Stalled Deals - Action Required</CardTitle>
                <CardDescription>Projects with no activity in 14+ days. Click a project to view details.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {data.stalled_deals.map((deal, i) => {
                    const risk = RISK_STYLES[deal.risk_level];
                    return (
                      <div
                        key={i}
                        className={`flex items-center gap-4 p-4 rounded-lg bg-white border ${risk.card} hover:bg-slate-50 cursor-pointer transition-colors`}
                        onClick={() => navigate(`/projects/${deal.project?.id}`)}
                        data-testid={`stalled-deal-${i}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1 flex-wrap">
                            <p className="font-semibold text-slate-800">{deal.contact_name}</p>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge className={`${risk.badge} cursor-help`}>{deal.risk_level} risk</Badge>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs text-sm p-3">
                                {deal.risk_level === "high" ? METRIC_DESCRIPTIONS.high_risk : METRIC_DESCRIPTIONS.medium_risk}
                              </TooltipContent>
                            </Tooltip>
                            <Badge variant="outline" className="capitalize">{deal.project?.stage?.replace(/_/g, " ")}</Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-slate-500">
                            {deal.project?.project_address && (
                              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{deal.project.project_address}</span>
                            )}
                            <span className="capitalize">{deal.project?.service_type?.replace(/_/g, " ")}</span>
                            <span className="font-medium text-slate-700">{formatCurrency(deal.project?.estimated_value || 0)}</span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className={`text-lg font-bold ${risk.icon}`}>{deal.days_stalled} days</p>
                          <p className="text-xs text-slate-400">stalled</p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          {deal.contact_phone && (
                            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); window.open(`tel:${deal.contact_phone}`); }} data-testid={`call-deal-${i}`}>
                              <Phone className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/projects/${deal.project?.id}`); }} data-testid={`view-deal-${i}`}>
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-16 text-center">
                <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-emerald-500" />
                </div>
                <h3 className="text-lg font-medium text-slate-700 mb-2">Pipeline is healthy!</h3>
                <p className="text-slate-500">No stalled deals detected. All projects have recent activity.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
